import React, { useState } from "react";
import Picker from "react-mobile-picker";
import Lottie from "lottie-react";
import { motion, AnimatePresence } from "framer-motion";
import { Radio } from "antd";
import dayjs from "dayjs";

import { ReactComponent as Edit } from "./../../../../assets/images/ddx-edit.svg";
import * as HandScroll from "./../../../../assets/animations/hand_scroll.json";
import Button from "../../../home/components/Button";

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const currentYear = new Date().getFullYear();
const years = Array.from({ length: 100 }, (_, i) =>
  (currentYear - i).toString()
);
const ages = Array.from({ length: 99 }, (_, i) => (i + 2).toString());

interface AgeInputProps {
  age: number | null;
  setAge: React.Dispatch<React.SetStateAction<number | null>>;
  disabled?: boolean;
  onPressEdit: () => void;
  onPressSend?: (
    prompt?: any,
    age?: number,
    dob?: { day: number; month: number; year: number }
  ) => void;
}

const AgeInput: React.FC<AgeInputProps | any> = ({
  age,
  setAge,
  disabled = false,
  onPressEdit,
  handleSubmit,
}) => {
  const [activeTab, setActiveTab] = useState<"age" | "dob">("dob");
  const [pickerValue, setPickerValue] = useState({
    age: age?.toString() ?? "18",
  });
  const [dobValue, setDobValue] = useState({
    month: months[new Date().getMonth()],
    date: new Date().getDate().toString(),
    year: currentYear.toString(),
  });
  const [tempValue, setTempValue] = useState<any>(null);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);

  // Helper to get month index
  const getMonthIndex = (month: string) =>
    months.findIndex((m) => m === month) + 1;

  const getDaysInMonth = (month: string, year: string) => {
    const monthIndex = months.findIndex((m) => m === month);
    const y = parseInt(year, 10);
    if (monthIndex === 1) {
      // February
      // Leap year check
      if ((y % 4 === 0 && y % 100 !== 0) || y % 400 === 0) {
        return 29;
      }
      return 28;
    }
    // Months with 31 days: Jan, Mar, May, Jul, Aug, Oct, Dec
    if ([0, 2, 4, 6, 7, 9, 11].includes(monthIndex)) return 31;
    // Months with 30 days: Apr, Jun, Sep, Nov
    return 30;
  };

  const renderState = () => {
    switch (age !== null) {
      case true:
        return (
          <motion.div
            key={`answered-${age}`}
            initial={{ opacity: 0, scale: 0.8, x: 40 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            exit={{ opacity: 0, scale: 0.8, x: 40 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="flex flex-row items-center gap-3 my-7 justify-end w-full"
          >
            <motion.button
              onClick={() => {
                onPressEdit();
                setTempValue(age);
                setIsEditMode(true);
                setAge(null);
                setPickerValue({ age: "18" });
              }}
              className="flex items-center justify-center w-10 h-10 rounded-full shadow-none border-none"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              <Edit className="w-6 h-6 text-[#4B4AD5]" />
            </motion.button>
            <motion.div
              className="bg-white rounded-2xl px-5 py-2 shadow-[0px_0px_12px_0px_#A238FF0D] flex items-center"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              {typeof age === "number" ? (
                <>
                  <span className="font-bold text-[#222]">Age:</span>
                  <span className="ml-1 text-[#393944] font-normal">
                    {age} years
                  </span>
                </>
              ) : (
                <>
                  <span className="font-bold text-[#222]">DOB:</span>
                  <span className="ml-1 text-[#393944] font-normal">{age}</span>
                </>
              )}
            </motion.div>
          </motion.div>
        );
      case false:
        return (
          <motion.div
            key={"unanswered"}
            initial={{
              opacity: 0,
              scale: 0.5,
              transformOrigin: "top right",
            }}
            animate={{ opacity: 1, scale: 1, transformOrigin: "top right" }}
            exit={{ opacity: 0, scale: 0.5, transformOrigin: "top right" }}
            transition={{ duration: 0.2 }}
            className="bg-white ml-7 my-7 shadow-[0px_0px_12px_0px_#A238FF0D] rounded-l-[18px] rounded-tr-[18px] p-3.5 flex flex-col"
          >
            {/* Tab Switcher */}
            <div className="flex w-full items-center rounded-xl border overflow-hidden">
              <Radio.Group
                value={activeTab}
                onChange={(e) => setActiveTab(e.target.value as "age" | "dob")}
                className="rounded-xl border border-[#4B4AD5] overflow-hidden"
                style={{ width: "100%", display: "flex" }}
              >
                {/* <Radio.Button
                  value="age"
                  style={{
                    width: "50%",
                    height: "41px",
                    padding: "5px 0",
                    // borderRadius: "16px 0 0 16px",
                    fontWeight: activeTab === "age" ? "bold" : "normal",
                    color: activeTab === "age" ? "#4B4AD5" : "#222",
                    background: activeTab === "age" ? "#F7F7FF" : "#fff",
                    borderLeft: "none",
                    boxShadow: "none",
                  }}
                  className="custom-radio-button flex justify-center border-0 border-r"
                >
                  Select Your Age
                </Radio.Button> */}
                <Radio.Button
                  value="dob"
                  style={{
                    width: "100%",
                    height: "41px",
                    padding: "5px 0",
                    borderRadius: "0 16px 16px 0",
                    fontWeight: activeTab === "dob" ? "bold" : "normal",
                    color: activeTab === "dob" ? "#4B4AD5" : "#222",
                    background: activeTab === "dob" ? "#F7F7FF" : "#fff",
                    border: "none",
                    boxShadow: "none",
                  }}
                  className="custom-radio-button flex justify-center border-0 border-r"
                >
                  Select Your DOB
                </Radio.Button>
              </Radio.Group>
            </div>
            {/* Picker Wheel */}
            <div className="flex justify-center relative">
              {activeTab === "age" ? (
                <>
                  <Picker
                    value={pickerValue}
                    onChange={setPickerValue}
                    wheelMode="natural"
                  >
                    <Picker.Column key={"age"} name={"age"}>
                      {ages.map((option) => {
                        const isSelected = option === pickerValue.age;
                        return (
                          <Picker.Item
                            key={option}
                            value={option}
                            className={
                              isSelected
                                ? "font-medium text-[#393944] text-base opacity-100 mx-4"
                                : "font-normal text-[#393944] text-sm opacity-40 mx-4"
                            }
                          >
                            {option}
                          </Picker.Item>
                        );
                      })}
                    </Picker.Column>
                  </Picker>
                  <Lottie
                    animationData={HandScroll}
                    autoPlay
                    loop
                    className="absolute z-10 top-1/2 left-[60%] h-11 w-11 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                  />
                </>
              ) : (
                <>
                  <Picker
                    value={dobValue}
                    onChange={(val) => {
                      // If month or year changes, adjust date if needed
                      const days = getDaysInMonth(
                        val.month || dobValue.month,
                        val.year || dobValue.year
                      );
                      let newDate = val.date || dobValue.date;
                      if (parseInt(newDate, 10) > days) {
                        newDate = days.toString();
                      }
                      setDobValue({
                        month: val.month || dobValue.month,
                        date: newDate,
                        year: val.year || dobValue.year,
                      });
                    }}
                    wheelMode="natural"
                  >
                    <Picker.Column key="month" name="month">
                      {months.map((option) => {
                        const isSelected = option === dobValue.month;
                        return (
                          <Picker.Item
                            key={option}
                            value={option}
                            className={
                              isSelected
                                ? "font-medium text-[#393944] text-base opacity-100 mx-4"
                                : "font-normal text-[#393944] text-sm opacity-40 mx-4"
                            }
                          >
                            {option}
                          </Picker.Item>
                        );
                      })}
                    </Picker.Column>
                    <Picker.Column key="date" name="date">
                      {Array.from(
                        {
                          length: getDaysInMonth(dobValue.month, dobValue.year),
                        },
                        (_, i) => (i + 1).toString()
                      ).map((option) => {
                        const isSelected = option === dobValue.date;
                        return (
                          <Picker.Item
                            key={option}
                            value={option}
                            className={
                              isSelected
                                ? "font-medium text-[#393944] text-base opacity-100 mx-4"
                                : "font-normal text-[#393944] text-sm opacity-40 mx-4"
                            }
                          >
                            {option}
                          </Picker.Item>
                        );
                      })}
                    </Picker.Column>
                    <Picker.Column key="year" name="year">
                      {years.map((option) => {
                        const isSelected = option === dobValue.year;
                        return (
                          <Picker.Item
                            key={option}
                            value={option}
                            className={
                              isSelected
                                ? "font-medium text-[#393944] text-base opacity-100"
                                : "font-normal text-[#393944] text-sm opacity-40"
                            }
                          >
                            {option}
                          </Picker.Item>
                        );
                      })}
                    </Picker.Column>
                  </Picker>
                </>
              )}
            </div>
            {/* Action Button */}
            <Button
              label={activeTab === "age" ? "Select Age" : "Select DOB"}
              onClick={() => {
                if (activeTab === "age") {
                  setAge(parseInt(pickerValue.age));
                  handleSubmit(`${parseInt(pickerValue.age)} years`);
                  setIsEditMode(false);
                  setTempValue(null);
                } else {
                  const dobDate = dayjs(
                    `${dobValue.year}-${getMonthIndex(dobValue.month)
                      .toString()
                      .padStart(2, "0")}-${dobValue.date.padStart(2, "0")}`
                  );
                  const formattedDob = dobDate.format("DD MMM YYYY");
                  setAge(formattedDob);
                  handleSubmit(formattedDob);
                }
              }}
              className="text-sm mt-0"
            />
          </motion.div>
        );
      default:
        return <></>;
    }
  };

  return (
    <div className={`${disabled && "opacity-50 pointer-events-none"}`}>
      <AnimatePresence mode="wait">{renderState()}</AnimatePresence>
    </div>
  );
};

export default AgeInput;
